import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useHasRole } from '@/hooks/useRoles';
import { usePlatformPermissions } from '@/hooks/usePlatformPermissions';
import { useIsSystemAdmin } from '@/components/SystemAdminGuard';
import { useModuleAccess, type ModuleLevel } from '@/hooks/useModuleAccess';
import { useRecycleBinCount } from '@/features/recycle-bin';
import { useAnalyticsAccess, hasAnalyticsAccess } from '@/features/usage/hooks';
import { useLicenseAccess, hasLicenseAccess } from '@/features/license';
import { useAuth } from '@/lib/auth';
import { IDENTITY_PUBLIC_URL } from '@/lib/config';
import { UserSwitcherBlock } from '@vanguard-atlas/shared';
import { 
  LayoutDashboard, 
  Bot, 
  Sparkles,
  Store,
  Database, 
  Server, 
  Cpu,
  BarChart3,
  Shield,
  Trash2,
  Settings, 
  ChevronDown, 
  ChevronRight, 
  ChevronLeft,
  BadgeCheck,
  Radar,
  KeyRound,
  ScanSearch,
  Coins,
} from 'lucide-react';

/**
 * Navigation item type
 */
interface ModuleRequirement {
  module: string;
  level: ModuleLevel;
}

interface SidebarChild {
  id: string;
  labelKey: string;
  path: string;
  icon?: React.ComponentType<{ className?: string }>;
  adminOnly?: boolean;
  systemOnly?: boolean;
  requiresModule?: ModuleRequirement;
  granularRole?: string;
  analyticsOnly?: boolean;
  monitoringOnly?: boolean;
  licenseOnly?: boolean;
  external?: boolean;
}

interface NavItem {
  id: string;
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
  path?: string;
  adminOnly?: boolean;
  systemOnly?: boolean;
  requiresModule?: ModuleRequirement;
  granularRole?: string;
  analyticsOnly?: boolean;
  monitoringOnly?: boolean;
  licenseOnly?: boolean;
  external?: boolean;
  children?: SidebarChild[];
}

/**
 * Navigation configuration
 */
const navItems: NavItem[] = [
  { 
    id: 'dashboard', 
    labelKey: 'admin.nav.dashboard', 
    icon: LayoutDashboard, 
    path: '/' 
  },
  {
    id: 'dashboards-governance',
    labelKey: 'admin.nav.dashboardsGovernance',
    icon: BarChart3,
    path: '/dashboards-governance',
    adminOnly: true,
  },
  { 
    id: 'agents', 
    labelKey: 'admin.nav.agents', 
    icon: Bot,
    children: [
      { id: 'agents-list', labelKey: 'admin.nav.agentsList', path: '/agents' },
      { id: 'agents-create', labelKey: 'admin.nav.createAgent', path: '/agents/new' },
      { id: 'agents-hives', labelKey: 'admin.nav.hives', path: '/agents/hives' },
      { id: 'agents-delegations', labelKey: 'admin.nav.delegations', path: '/agents/delegations' },
      { id: 'skills', labelKey: 'admin.nav.skills', icon: Sparkles, path: '/skills' },
    ]
  },
  {
    id: 'frontier',
    labelKey: 'admin.nav.frontier',
    icon: Radar,
    requiresModule: { module: 'frontier', level: 'view' },
    children: [
      {
        id: 'frontier-overview',
        labelKey: 'admin.nav.frontierOverview',
        path: '/frontier',
        requiresModule: { module: 'frontier', level: 'view' },
      },
      {
        id: 'frontier-routines',
        labelKey: 'admin.nav.frontierRoutines',
        path: '/frontier/routines',
        requiresModule: { module: 'frontier', level: 'view' },
      },
      {
        id: 'frontier-runs',
        labelKey: 'admin.nav.frontierRuns',
        path: '/frontier/runs',
        requiresModule: { module: 'frontier', level: 'view' },
      },
      {
        id: 'frontier-inbox',
        labelKey: 'admin.nav.frontierInbox',
        path: '/frontier/inbox',
        requiresModule: { module: 'frontier', level: 'view' },
      },
      {
        id: 'frontier-events',
        labelKey: 'admin.nav.frontierEvents',
        path: '/frontier/events',
        requiresModule: { module: 'frontier', level: 'view' },
      },
    ],
  },

  {
    // Sprint 288 Stream E (REQ-GW-005/010) · Sprint 365 Stream A (CHG-GW-001):
    // Studio del AI Gateway con tres superficies. Visible con
    // Module:ai_gateway#view (fail-closed).
    id: 'developers',
    labelKey: 'admin.nav.developers',
    icon: KeyRound,
    path: '/ia-gateway',
    requiresModule: { module: 'ai_gateway', level: 'view' },
    children: [
      {
        id: 'gateway-applications',
        labelKey: 'admin.nav.gatewayApplications',
        path: '/ia-gateway',
        requiresModule: { module: 'ai_gateway', level: 'view' },
      },
      {
        id: 'gateway-ide',
        labelKey: 'admin.nav.gatewayIde',
        path: '/ia-gateway/ide',
        requiresModule: { module: 'ai_gateway', level: 'view' },
      },
      {
        id: 'gateway-policy',
        labelKey: 'admin.nav.gatewayPolicy',
        path: '/ia-gateway/policy',
        requiresModule: { module: 'ai_gateway', level: 'view' },
      },
    ],
  },

  {
    // Sprint 285 Stream G (REQ-MINT-012; LLD §7) + Sprint 286 Stream D
    // (REQ-MINT-006): superficies de consulta y decisión del Studio. La
    // visibilidad de tarifas la decide el backend (D10/SEC-022).
    id: 'model-intel',
    labelKey: 'admin.nav.modelIntel',
    icon: ScanSearch,
    requiresModule: { module: 'model-intel', level: 'view' },
    children: [
      {
        id: 'model-intel-panorama',
        labelKey: 'admin.nav.modelIntelPanorama',
        path: '/model-intel/panorama',
        requiresModule: { module: 'model-intel', level: 'view' },
      },
      {
        id: 'model-intel-compare',
        labelKey: 'admin.nav.modelIntelCompare',
        path: '/model-intel/compare',
        requiresModule: { module: 'model-intel', level: 'view' },
      },
      {
        id: 'model-intel-functions',
        labelKey: 'admin.nav.modelIntelFunctions',
        path: '/model-intel/functions',
        requiresModule: { module: 'model-intel', level: 'view' },
      },
      {
        id: 'model-intel-pricing',
        labelKey: 'admin.nav.modelIntelPricing',
        path: '/model-intel/pricing',
        requiresModule: { module: 'model-intel', level: 'view' },
      },
    ],
  },

  {
    id: 'klviar-marketplace',
    labelKey: 'admin.nav.klviarMarketplace',
    icon: Store,
    children: [
      { id: 'marketplace-browse', labelKey: 'admin.nav.klviarBrowse', path: '/marketplace' },
      { id: 'marketplace-publish', labelKey: 'admin.nav.klviarPublish', path: '/marketplace/publish' },
      { id: 'marketplace-approvals', labelKey: 'admin.nav.klviarApprovals', path: '/marketplace/approvals', granularRole: 'cac_approver' },
    ],
  },
  { 
    id: 'knowledge', 
    labelKey: 'admin.nav.knowledge', 
    icon: Database,
    children: [
      { id: 'knowledge-bases', labelKey: 'admin.nav.knowledgeBases', path: '/knowledge/bases' },
      { id: 'collections', labelKey: 'admin.nav.collections', path: '/knowledge/collections' },
      { id: 'taxonomies', labelKey: 'admin.nav.taxonomies', path: '/knowledge/taxonomies' },
      { id: 'documents', labelKey: 'admin.nav.documents', path: '/knowledge/documents' },
      { id: 'ingest', labelKey: 'admin.nav.ingest', path: '/knowledge/ingest' },
      { id: 'datasources', labelKey: 'admin.nav.dataSources', path: '/knowledge/datasources' },
    ]
  },
  { 
    id: 'integrations', 
    labelKey: 'admin.nav.integrations', 
    icon: Server, 
    children: [
      { id: 'mcp-servers', labelKey: 'admin.nav.mcpServers', path: '/integrations' },
      { id: 'channels', labelKey: 'admin.nav.channels', path: '/channels' },
      { id: 'mcp-catalog', labelKey: 'admin.nav.mcpCatalog', path: '/integrations/catalog' },
      { id: 'system-templates', labelKey: 'admin.nav.systemTemplates', path: '/integrations/templates', systemOnly: true },
    ]
  },

  {
    id: 'runners',
    labelKey: 'admin.nav.runners',
    icon: Cpu,
    requiresModule: { module: 'runner', level: 'view' },
    children: [
      { id: 'runners-list', labelKey: 'admin.nav.runnersList', path: '/integrations/runners' },
      { id: 'runner-policy-global', labelKey: 'admin.nav.runnerPolicyGlobal', path: '/integrations/runners/policies', requiresModule: { module: 'runner', level: 'admin' } },
      { id: 'runner-policy-profiles', labelKey: 'admin.nav.runnerPolicyProfiles', path: '/integrations/runners/profiles', requiresModule: { module: 'runner', level: 'admin' } },
      { id: 'download-agent', labelKey: 'admin.nav.downloadAgent', path: '/integrations/download-agent', requiresModule: { module: 'runner', level: 'view' } },
    ],
  },

  {
    id: 'governance',
    labelKey: 'admin.nav.governance',
    icon: Shield,
    children: [
      {
        id: 'usage',
        labelKey: 'admin.nav.usage',
        icon: BarChart3,
        path: '/usage',
        analyticsOnly: true,
      },
      {
        id: 'my-license',
        labelKey: 'admin.nav.myLicense',
        icon: BadgeCheck,
        path: '/license',
        // Gated by GET /api/v1/licenses/me (200 → visible; 403/error → hidden).
        // License#view is seeded to Tenant#owners + Tenant#admins (REQ-BIL-018).
        licenseOnly: true,
      },
      {
        id: 'secret-vault',
        labelKey: 'admin.nav.secretVault',
        path: '/secrets',
        // ISS-471: visible para TODOS los tenants (permiso granular del módulo;
        // por defecto todos los roles pueden USAR secretos — manage para
        // admins). La licencia la impone el backend (feature guard).
        requiresModule: { module: 'secrets', level: 'view' },
      },
      {
        id: 'data-governance',
        labelKey: 'admin.nav.dataGovernance',
        path: '/data-governance',
        // ISS-478: accesible solo con el permiso granular del módulo
        // configuración (Module:config#view); la sección de plataforma se
        // oculta en la página si no es system.
        requiresModule: { module: 'config', level: 'view' },
      },
      {
        id: 'system-tenants',
        labelKey: 'admin.nav.systemTenants',
        path: '/system/tenants',
        systemOnly: true,
      },
      {
        // Sprint 286 Stream I: vista de plataforma de precios (agregados M2M
        // por DR; sin identificadores de tenant).
        id: 'system-model-pricing',
        labelKey: 'admin.nav.systemModelPricing',
        path: '/system/model-pricing',
        systemOnly: true,
      },
      {
        id: 'security-alerts',
        labelKey: 'admin.nav.securityAlerts',
        path: '/security/alerts',
      },
      {
        // 19-Oct-2026 (REQ-CHAT-039 §2/§7): panel de ahorro de tokens del
        // tenant — gobernanza de coste, movido desde Settings.
        id: 'token-savings',
        labelKey: 'admin.nav.tokenSavings',
        icon: Coins,
        path: '/governance/token-savings',
        adminOnly: true,
      },
      {
        // Gobierno del uso de modelos (tenant-local): tokens/peticiones por
        // modelo; sin agentes y sin CP. Permiso analytics#view.
        id: 'model-usage-governance',
        labelKey: 'admin.nav.modelUsageGovernance',
        icon: BarChart3,
        path: '/governance/model-usage',
        requiresModule: { module: 'analytics', level: 'view' },
      },
      {
        // Sprint 285 Stream G (REQ-MINT-013): panel precios vs costes; la
        // visibilidad de tarifas la decide el backend (D10/SEC-022).
        id: 'model-pricing',
        labelKey: 'admin.nav.modelPricing',
        icon: Coins,
        path: '/governance/model-pricing',
        requiresModule: { module: 'model-intel', level: 'view' },
      },
      {
        // Flower (Celery monitoring) — same-origin subpath behind Oathkeeper
        // OPL rules (MonitoringAccess:platform#view). Opens in a new tab.
        id: 'workers',
        labelKey: 'admin.nav.workers',
        path: '/flower/',
        external: true,
        monitoringOnly: true,
      },
    ],
  },
  { 
    id: 'recycle-bin', 
    labelKey: 'admin.nav.recycleBin', 
    icon: Trash2, 
    path: '/recycle-bin' 
  },
  { 
    id: 'settings', 
    labelKey: 'admin.nav.settings', 
    icon: Settings, 
    path: '/settings' 
  },
];

/**
 * Sidebar component with navigation
 * 
 * Features:
 * - Collapsible sidebar
 * - Expandable navigation sections
 * - Active route highlighting
 * - Responsive design
 */
interface SidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const { t } = useI18n();
  const location = useLocation();
  const isAdmin = useHasRole('admin');
  const isSystemAdmin = useIsSystemAdmin();
  const { roles: platformRoles, monitoring } = usePlatformPermissions();
  const { user, logout } = useAuth();
  const [expandedItems, setExpandedItems] = useState<string[]>(['agents', 'knowledge']);
  const recycleBinCount = useRecycleBinCount();
  const { data: analyticsAccess } = useAnalyticsAccess();
  const { data: licenseAccess } = useLicenseAccess();
  const canViewAnalytics = hasAnalyticsAccess(analyticsAccess);
  const canViewMonitoring = monitoring?.view === true;
  const canViewLicense = hasLicenseAccess(licenseAccess);

  const runnerView = useModuleAccess('runner', 'view');
  const runnerAdmin = useModuleAccess('runner', 'admin');
  const dataGovernanceView = useModuleAccess('config', 'view');
  const secretsView = useModuleAccess('secrets', 'view');
  const frontierView = useModuleAccess('frontier', 'view');
  const gatewayView = useModuleAccess('ai_gateway', 'view');
  const modelIntelView = useModuleAccess('model-intel', 'view');

  const moduleAccessKey = (req: ModuleRequirement): boolean => {
    if (req.module === 'runner' && req.level === 'view') return runnerView;
    if (req.module === 'runner' && req.level === 'admin') return runnerAdmin;
    if (req.module === 'config' && req.level === 'view') return dataGovernanceView;
    if (req.module === 'secrets' && req.level === 'view') return secretsView;
    if (req.module === 'frontier' && req.level === 'view') return frontierView;
    if (req.module === 'ai_gateway' && req.level === 'view') return gatewayView;
    if (req.module === 'model-intel' && req.level === 'view') return modelIntelView;
    return false;
  };

  const visibleNavItems = navItems.filter((item) => {
    if (item.adminOnly && !isAdmin) return false;
    if (item.systemOnly && !isSystemAdmin) return false;
    if (item.requiresModule && !moduleAccessKey(item.requiresModule)) return false;
    if (item.granularRole && !platformRoles.includes(item.granularRole) && !isSystemAdmin) return false;
    if (item.analyticsOnly && !canViewAnalytics) return false;
    if (item.monitoringOnly && !canViewMonitoring) return false;
    if (item.licenseOnly && !canViewLicense) return false;
    return true;
  });

  const toggleExpand = (id: string) => {
    setExpandedItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const isParentActive = (item: NavItem) => {
    if (item.path) return isActive(item.path);
    return item.children?.some(child => isActive(child.path)) ?? false;
  };

  const visibleChildren = (item: NavItem): SidebarChild[] =>
    (item.children ?? []).filter((child) => {
      if (child.adminOnly && !isAdmin) return false;
      if (child.systemOnly && !isSystemAdmin) return false;
      if (child.requiresModule && !moduleAccessKey(child.requiresModule)) return false;
      if (child.granularRole && !platformRoles.includes(child.granularRole) && !isSystemAdmin) return false;
      if (child.analyticsOnly && !canViewAnalytics) return false;
      if (child.monitoringOnly && !canViewMonitoring) return false;
      if (child.licenseOnly && !canViewLicense) return false;
      return true;
    });

  // Longest-prefix match: `/ia-gateway` is a prefix of `/ia-gateway/ide`, so the
  // shortest path must not stay highlighted on nested routes.
  const activeChildPath = (item: NavItem): string | undefined =>
    visibleChildren(item)
      .filter((child) => isActive(child.path))
      .sort((a, b) => b.path.length - a.path.length)[0]?.path;

  return (
    <aside 
      className={cn(
        'bg-card border-r flex flex-col h-full transition-all duration-300',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Logo */}
      <div className="p-4 border-b">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg ring-1 ring-slate-200 bg-white flex items-center justify-center flex-shrink-0 overflow-hidden">
            <img src="/favicon.svg" alt="Klviar" className="w-6 h-6" />
          </div>
          {!collapsed && (
            <div>
              <div className="font-bold brand-display">Klviar</div>
              <div className="text-xs text-muted-foreground">Klviar Studio</div>
            </div>
          )}
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {visibleNavItems.map((item) => {
          const Icon = item.icon;
          
          return (
            <div key={item.id}>
              {item.children ? (
                // Expandable section
                <>
                  <Button
                    variant="ghost"
                    onClick={() => toggleExpand(item.id)}
                    className={cn(
                      'w-full justify-start',
                      isParentActive(item) && 'bg-accent'
                    )}
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                    {!collapsed && (
                      <>
                        <span className="flex-1 text-left">
                          {t(item.labelKey)}
                        </span>
                        {expandedItems.includes(item.id) ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </>
                    )}
                  </Button>
                  
                  {!collapsed && expandedItems.includes(item.id) && (
                    <div className="ml-8 mt-1 space-y-1">
                      {visibleChildren(item).map((child) => (
                        child.external ? (
                          <a
                            key={child.id}
                            href={child.path}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <Button
                              variant="ghost"
                              className="w-full justify-start text-sm"
                              size="sm"
                            >
                              {t(child.labelKey)}
                            </Button>
                          </a>
                        ) : (
                        <Link
                          key={child.id}
                          to={child.path}
                        >
                          <Button
                            variant={activeChildPath(item) === child.path ? 'default' : 'ghost'}
                            className="w-full justify-start text-sm"
                            size="sm"
                          >
                            {t(child.labelKey)}
                          </Button>
                        </Link>
                        )
                      ))}
                    </div>
                  )}
                </>
              ) : (
                // Simple link
                <Link to={item.path!}>
                  <Button
                    variant={isActive(item.path) ? 'default' : 'ghost'}
                    className="w-full justify-start"
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                    {!collapsed && (
                      <>
                        <span className="flex-1 text-left">{t(item.labelKey)}</span>
                        {item.id === 'recycle-bin' && recycleBinCount > 0 && (
                          <span className="ml-auto inline-flex items-center justify-center rounded-full bg-destructive/15 text-destructive text-xs font-medium px-1.5 min-w-[1.25rem] h-5">
                            {recycleBinCount}
                          </span>
                        )}
                      </>
                    )}
                  </Button>
                </Link>
              )}
            </div>
          );
        })}
      </nav>

      {/* Collapse Toggle */}
      {onToggle && (
        <div className="p-3 border-t">
          <Button
            variant="ghost"
            onClick={onToggle}
            className="w-full justify-center"
            size="sm"
          >
            {collapsed ? (
              <ChevronRight className="h-5 w-5" />
            ) : (
              <>
                <ChevronLeft className="h-5 w-5" />
                <span>{t('admin.nav.collapse')}</span>
              </>
            )}
          </Button>
        </div>
      )}

      {/* User Switcher */}
      {user && (
        <div className={cn('border-t', collapsed ? 'p-2' : 'p-3')}>
          <UserSwitcherBlock
            user={user}
            variant="studio"
            identityPublicUrl={IDENTITY_PUBLIC_URL}
            onLogout={logout}
            collapsed={collapsed}
            labels={{
              switchStudio: t('common.switchStudio'),
              openChat: t('common.openChat'),
              accountSettings: t('common.accountSettings'),
              logout: t('common.logout'),
              user: t('common.user'),
              userMenu: t('common.userMenu'),
            }}
          />
        </div>
      )}
    </aside>
  );
}
